import React, { Component } from 'react';
import moment from 'moment';
import axios from 'axios';
import { authService } from '../../services/authService';

import { Table, Tag, Button, Row, Col, Spin } from 'antd';
import { SafetyCertificateOutlined } from '@ant-design/icons';

import AcknowledgePanelModal from './AcknowledgePanelModal';
import { API_BASE_URL } from '../../config/api';

class PanelAcknowledgement extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      panels: [],
      physicians: [],
      clientCode: props.client.ClientCode,
      signingPanel: null,
    };
  }

  componentDidMount = () => {
    this.loadData();
    this.loadPhysicians();
  }

  loadPhysicians = async () => {
    const token = await authService.ensureValidToken();
    if (!token) {
      console.error('Not authenticated');
      return;
    }
    axios({
      method: 'GET',
      url: `${API_BASE_URL}/connect/physicians/search`,
      params: { clientCode: this.state.clientCode },
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      this.setState({ physicians: res.data.physicians });
    }).catch(err => {
      console.log(err);
    });
  }

  // No filters here on purpose: this screen always shows every requested
  // acknowledgement, across all years that have one, so nothing actionable
  // can be hidden. Each row carries its year — signing must write to that
  // year's request, not blindly to the current year.
  loadData = async () => {
    this.setState({ loading: true });
    const token = await authService.ensureValidToken();
    if (!token) {
      console.error('Not authenticated');
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const yearsRes = await axios({
        method: 'GET',
        url: `${API_BASE_URL}/connect/panels/${this.state.clientCode}/years`,
        headers,
      });
      const years = yearsRes.data.years || [];

      const lists = await Promise.all(years.map(year =>
        axios({
          method: 'GET',
          url: `${API_BASE_URL}/connect/panels/${this.state.clientCode}`,
          params: { year },
          headers,
        }).then(res => (res.data.panels || []).map(p => ({ ...p, year })))
      ));

      // Generic action-item shape: every item type rendered here carries
      // itemType + itemName, so future types (custom reference ranges, ...)
      // just map their rows into the same shape and concat into this list.
      // Only OPEN items are shown — once signed, an item leaves this screen
      // (signed history stays available in the admin portal). Newest year first.
      const panels = lists.flat()
        .filter(p => !p.acknowledged)
        .map(p => ({ ...p, itemType: 'Custom Panel', itemName: `(${p.PanelCode}) ${p.PanelName}` }))
        .sort((a, b) => b.year - a.year);
      this.setState({ panels, loading: false });
      if (this.props.onCount) this.props.onCount(panels.length);
    } catch (err) {
      console.log(err);
      this.setState({ loading: false });
    }
  }

  onSigned = (panel) => {
    // The screen only lists open items — a signed one simply leaves the list.
    this.setState(prev => ({
      panels: prev.panels.filter(p =>
        !(p.PanelCode === panel.PanelCode && p.year === panel.year)
      ),
      signingPanel: null,
    }), () => { if (this.props.onCount) this.props.onCount(this.state.panels.length); });
  }

  render() {
    const columns = [
      {
        // Mobile-only: item name stacked over its type/status tags, so the
        // table fits a phone without sideways scrolling.
        title: 'Item',
        key: 'itemMobile',
        responsive: ['xs'],
        render: (_, record) => (
          <div style={{ lineHeight: 1.4 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{record.itemName}</div>
            <Tag color="blue">{record.itemType}</Tag>
            <Tag color="orange">Action Required</Tag>
          </div>
        ),
      },
      {
        title: 'Type',
        dataIndex: 'itemType',
        key: 'itemType',
        width: 160,
        responsive: ['sm'],
        render: (t) => <Tag color="blue">{t}</Tag>,
      },
      {
        title: 'Item',
        dataIndex: 'itemName',
        key: 'itemName',
        responsive: ['sm'],
      },
      {
        title: 'Status',
        key: 'status',
        width: 260,
        responsive: ['sm'],
        render: (_, record) =>
          record.acknowledged ? (
            <Tag color="green">
              Signed{record.acknowledgedBy ? ` by ${record.acknowledgedBy}` : ''}
              {record.acknowledgedAt ? ` on ${moment(record.acknowledgedAt).format('MM/DD/YYYY')}` : ''}
            </Tag>
          ) : (
            <Tag color="orange">Action Required</Tag>
          ),
      },
      {
        title: '',
        key: 'actions',
        width: 130,
        render: (_, record) => (
          <Button
            size="small"
            type={record.acknowledged ? 'default' : 'primary'}
            onClick={() => this.setState({ signingPanel: record })}
          >
            {record.acknowledged ? 'View' : 'Review & Sign'}
          </Button>
        ),
      },
    ];

    return (
      <Row justify="center">
        <Col xs={24} md={this.props.onCount ? 24 : 16}>
          {this.state.loading ? (
            <div style={{ textAlign: 'center' }}>
              <Spin size="large" />
            </div>
          ) : (
            <Table
              dataSource={this.state.panels}
              columns={columns}
              rowKey={(record) => `${record.itemType}-${record.PanelCode}-${record.year}`}
              size="small"
              pagination={false}
              locale={{
                emptyText: (
                  <div style={{ padding: '32px 0', color: '#888' }}>
                    <SafetyCertificateOutlined style={{ fontSize: 24, marginBottom: 8, display: 'block' }} />
                    No items currently require your attention
                  </div>
                ),
              }}
            />
          )}

          {this.state.signingPanel && (
            <AcknowledgePanelModal
              client={this.props.client}
              panel={this.state.signingPanel}
              physicians={this.state.physicians}
              onClose={() => this.setState({ signingPanel: null })}
              onSigned={this.onSigned}
            />
          )}
        </Col>
      </Row>
    );
  }
}

export default PanelAcknowledgement;
